import React from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Toolbar,
  TextField,
  InputAdornment,
  Button,
  Box,
  Chip,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import type { AppDataGridProps } from "./types";
import { AppDataGridFooter } from "./AppDataGridFooter";
import { AppDataGridEmptyState } from "./AppDataGridEmptyState";
import isMobile from "is-mobile";

export const AppDataGrid: React.FC<AppDataGridProps> = ({
  columns,
  rows,
  tableLabel,
  selectedRowIds,
  onSelectRow,
  onSelectAll,
  pagination,
  bulkActionsBarRenderer,
  noDataIcon,
  noDataTitle,
  noDataDescription,
  onOpenFilters,
  searchTerm,
  onSearchTermChange,
  searchPlaceholder,
  onSearchClick,
  filterChips = [],
  filterCount,
  isLoading = false,
}) => {
  const numSelected = selectedRowIds?.length ?? 0;
  const rowCount = rows.length;
  const isAllSelected = rowCount > 0 && numSelected === rowCount;
  const isIndeterminate = numSelected > 0 && numSelected < rowCount;
  const appliedCount = filterCount ?? filterChips.length; // contador interno do botão

  return (
    <Paper
      sx={{
        width: "100%",
        overflow: "hidden",
        fontFamily: "Roboto, sans-serif",
        position: "relative",
        minHeight: isLoading ? "300px" : "auto",
      }}
    >
      {isLoading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            zIndex: 2,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {numSelected > 0 && bulkActionsBarRenderer ? (
        bulkActionsBarRenderer(selectedRowIds!)
      ) : (
        <>
          <Toolbar
            sx={{
              display: "flex",
              flexDirection: isMobile() ? "column" : "row",
              pl: { sm: 2 },
              pr: { xs: 1, sm: 1 },
            }}
          >
            {onSearchTermChange && (
              <Box
                sx={{
                  display: "flex",
                  flex: 1,
                  gap: 1,
                  mr: isMobile() ? 0 : 2,
                }}
              >
                <TextField
                  size="small"
                  variant="outlined"
                  placeholder={
                    searchPlaceholder ??
                    `Buscar por nome, localidade ou email...`
                  }
                  sx={{ flex: 1 }}
                  value={searchTerm}
                  onChange={(e) => onSearchTermChange?.(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && onSearchClick) onSearchClick();
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                {onSearchClick && (
                  <Button
                    variant="contained"
                    onClick={onSearchClick}
                    sx={{ textTransform: "none" }}
                  >
                    Buscar
                  </Button>
                )}
              </Box>
            )}
            {onOpenFilters && (
              <Button
                variant="outlined"
                startIcon={<FilterListIcon />}
                onClick={onOpenFilters}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  ml: onSearchTermChange ? 0 : "auto",
                  mt: isMobile() ? "1vh" : "0px",
                }}
                fullWidth={isMobile()}
              >
                Filtros
                {appliedCount > 0 && (
                  <Box
                    component="span"
                    sx={{
                      ml: 1,
                      px: 1,
                      height: 22,
                      minWidth: 22,
                      borderRadius: "999px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "#E8F0FE",
                      color: "#1a73e8",
                      fontSize: 12,
                      fontWeight: 700,
                      lineHeight: 1,
                    }}
                  >
                    {appliedCount}
                  </Box>
                )}
              </Button>
            )}
          </Toolbar>

          {filterChips.length > 0 && (
            <Box
              sx={{
                px: 2,
                pb: 1,
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                mt: isMobile() ? "8px" : "0px",
              }}
            >
              {filterChips.map((chip) => (
                <Chip
                  key={chip.id}
                  label={chip.label}
                  onDelete={chip.onDelete}
                  color="primary"
                  variant="outlined"
                  sx={{
                    bgcolor: "#E8F0FE",
                    "& .MuiChip-deleteIcon": { color: "#1a73e8" },
                  }}
                />
              ))}
            </Box>
          )}
        </>
      )}

      {rowCount > 0 ? (
        <>
          <TableContainer>
            <Table stickyHeader aria-label="collapsible table">
              <TableHead>
                <TableRow>
                  {onSelectAll && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        indeterminate={isIndeterminate}
                        checked={isAllSelected}
                        onChange={(e) => onSelectAll(e.target.checked)}
                        inputProps={{ "aria-label": "select all items" }}
                      />
                    </TableCell>
                  )}
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      style={{ minWidth: column.minWidth }}
                      sx={{
                        display: column.hideOnMobile
                          ? { xs: "none", md: "table-cell" }
                          : "table-cell",
                      }}
                    >
                      {column.header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    role={onSelectRow ? "checkbox" : "row"}
                    aria-checked={
                      onSelectRow ? selectedRowIds!.includes(row.id) : undefined
                    }
                    tabIndex={-1}
                    selected={
                      onSelectRow ? selectedRowIds!.includes(row.id) : false
                    }
                  >
                    {onSelectRow && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          checked={selectedRowIds!.includes(row.id)}
                          onChange={() => onSelectRow(row.id)}
                          inputProps={{
                            "aria-labelledby": `checkbox-${row.id}`,
                          }}
                        />
                      </TableCell>
                    )}
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.align}
                        sx={{
                          display: column.hideOnMobile
                            ? { xs: "none", md: "table-cell" }
                            : "table-cell",
                        }}
                      >
                        {row.render[column.id]}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <AppDataGridFooter pagination={pagination} tableLabel={tableLabel} />
        </>
      ) : (
        !isLoading && (
          <AppDataGridEmptyState
            icon={noDataIcon}
            title={noDataTitle}
            description={noDataDescription}
          />
        )
      )}
    </Paper>
  );
};
